import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import { Field, Form, Formik } from 'formik'

import { media } from 'utilities/styled'

import {
  FormikSelectInput,
  FormikTextInput,
} from 'components/molecules/formik-field'
import Typography from 'components/molecules/typography'
import { GpsrTypeEnum } from 'config/enums'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.sizings.lvl2} ${({ theme }) => theme.sizings.lvl3};
  max-width: ${({ theme }) => theme.sizeByFactor(80)};

  ${media.tablet`
    grid-template-columns: 1fr 1fr;
  `}
`

const StyledDropdown = styled(FormikSelectInput)`
  background-color: ${({ theme }) => theme.colors.fieldBackground};
`

const StyledTextInput = styled(FormikTextInput)`
  background-color: ${({ theme }) => theme.colors.fieldBackground};
`

const StyledForm = styled(Form)`
  width: 100%;
`

const FormSection = styled.div`
  margin-top: ${({ theme }) => theme.sizings.lvl3};
`

const INITIAL_VALUES = {
  type: '',
  manufacturerName: '',
  address: '',
  place: '',
  province: '',
  postalCode: '',
  phoneNumber: '',
  website: '',
  email: '',
}

const TYPE_OPTIONS = (t) => [
  { label: t('gpsrForm.manufacturer'), value: GpsrTypeEnum.MANUFACTURER },
  { label: t('gpsrForm.representative'), value: GpsrTypeEnum.REPRESENTATIVE },
]

/**
 * GpsrDialogForm uses Formik with innerRef so we can trigger submit from parent component
 */
const GpsrDialogForm = React.forwardRef(
  ({ onSubmit, validationSchema, onFormState }, ref) => {
    const { t } = useTranslation()

    return (
      <Formik
        innerRef={ref}
        initialValues={INITIAL_VALUES}
        onSubmit={onSubmit}
        validateOnMount
        validationSchema={validationSchema}
      >
        {(formik) => {
          if (onFormState) {
            onFormState(formik)
          }
          return (
            <StyledForm>
              <Typography type="Level2Heading">{t('contactData')}</Typography>
              <Typography type="Level3Heading">
                {t('gpsrForm.noData')}
              </Typography>

              <Container>
                <Field
                  name="type"
                  component={StyledDropdown}
                  label={t('gpsrForm.typeLabel')}
                  required
                  items={TYPE_OPTIONS(t)}
                  filled
                />
                <Field
                  name="manufacturerName"
                  component={StyledTextInput}
                  label={t('name')}
                  required
                  filled
                />
              </Container>

              <FormSection>
                <Typography type="Level3Heading">
                  {t('gpsrForm.address')}
                </Typography>
                <Container>
                  <Field
                    name="place"
                    component={StyledTextInput}
                    label={t('place')}
                    required
                    filled
                  />
                  <Field
                    name="address"
                    component={StyledTextInput}
                    label={t('gpsrForm.streetName')}
                    required
                    filled
                  />
                  <Field
                    name="province"
                    component={StyledTextInput}
                    label={t('gpsrForm.province')}
                    required
                    filled
                  />
                  <Field
                    name="postalCode"
                    component={StyledTextInput}
                    label={t('postalcode')}
                    required
                    filled
                  />
                </Container>
              </FormSection>

              <FormSection>
                <Typography type="Level3Heading">
                  {t('gpsrForm.contactDetails')}
                </Typography>
                <Container>
                  <Field
                    name="phoneNumber"
                    component={StyledTextInput}
                    label={t('gpsrForm.phoneNumber')}
                    required
                    filled
                  />
                  <Field
                    name="website"
                    component={StyledTextInput}
                    label={t('gpsrForm.website')}
                    filled
                  />
                  <Field
                    name="email"
                    component={StyledTextInput}
                    label={t('email')}
                    required
                    filled
                  />
                </Container>
              </FormSection>
            </StyledForm>
          )
        }}
      </Formik>
    )
  },
)

GpsrDialogForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.object,
  onFormState: PropTypes.func,
}

export default GpsrDialogForm
